import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

// "Samples" gift — added whenever the cart has any item, no threshold.
const GIFT_VARIANT_ID = 54146920284493;

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    const sectionId = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (sectionId) ids.add(sectionId);
  });
  return Array.from(ids);
}

function applySections(sections, sectionIds) {
  if (!sections) return;
  sectionIds.forEach((id) => {
    if (sections[id]) {
      morphSection(id, sections[id]);
    }
  });
}

async function addGift(sectionIds) {
  const body = JSON.stringify({
    id: GIFT_VARIANT_ID,
    quantity: 1,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch('/cart/add.js', fetchConfig('json', { body }));
  const data = await response.json();
  if (data.errors) {
    console.error('kc-auto-gift add failed', data.errors);
    return;
  }
  applySections(data.sections, sectionIds);
}

async function removeGift(sectionIds) {
  const body = JSON.stringify({
    id: GIFT_VARIANT_ID,
    quantity: 0,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const data = await response.json();
  if (data.errors) {
    console.error('kc-auto-gift remove failed', data.errors);
    return;
  }
  applySections(data.sections, sectionIds);
}

let isProcessing = false;
let hasPendingEvent = false;

/**
 * Syncs the gift line item with the cart's current contents.
 * @param {Object|null} cartSnapshot - Cart JSON already available from the triggering
 *   event (cart:update carries the full cart on quantity/remove changes). When present,
 *   this skips the extra GET /cart.js round trip that used to run on every single cart
 *   change. Falls back to fetching /cart.js when no usable snapshot is available (e.g.
 *   the add-to-cart event only carries the added line, or on the queued replay after a
 *   gift add/remove, where we need a guaranteed-fresh read).
 */
async function syncGift(cartSnapshot) {
  if (isProcessing) {
    hasPendingEvent = true;
    return;
  }
  isProcessing = true;

  try {
    const cart = cartSnapshot && Array.isArray(cartSnapshot.items) ? cartSnapshot : await (await fetch('/cart.js')).json();

    const nonGiftCount = cart.items
      .filter((item) => item.variant_id !== GIFT_VARIANT_ID)
      .reduce((sum, item) => sum + item.quantity, 0);
    const hasGift = cart.items.some((item) => item.variant_id === GIFT_VARIANT_ID);
    const shouldHaveGift = nonGiftCount > 0;

    if (shouldHaveGift === hasGift) return;

    const sectionIds = getCartSectionIds();

    if (shouldHaveGift && !hasGift) {
      await addGift(sectionIds);
    } else if (!shouldHaveGift && hasGift) {
      await removeGift(sectionIds);
    }
  } catch (error) {
    console.error('kc-auto-gift error', error);
  } finally {
    isProcessing = false;
    if (hasPendingEvent) {
      hasPendingEvent = false;
      // No snapshot passed here on purpose: our own add/remove above (or whatever
      // fired while we were busy) means any snapshot we're holding could be stale,
      // so this replay always reads fresh state.
      syncGift();
    }
  }
}

let debounceTimer = null;
let pendingSnapshot = null;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const resource = event && event.detail && event.detail.resource;
  pendingSnapshot = resource && Array.isArray(resource.items) ? resource : null;

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => syncGift(pendingSnapshot), 150);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncGift());
} else {
  syncGift();
}
